import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Paper,
  Portal,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowDropUp } from '@mui/icons-material';
import { icons } from '@dpdgroupuk/cc-dpd-ui';

import { SUMMARY_DETAILS } from '../../constants/strings';
import TotalPrice from './components/TotalPrice';
import CheckoutPriceList from './components/CheckoutPriceList';

const StyledAccordionSummary = styled(AccordionSummary)({
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
  '& .MuiAccordionSummary-content': {
    justifyContent: 'flex-end',
  },
});

const paymentIcons = [
  { Icon: icons.GooglePay, sx: { width: 40, height: 22 } },
  { Icon: icons.ApplePay, sx: { width: 34, height: 22 } },
  { Icon: icons.PayPal, darkModeWhite: true, sx: { width: 82, height: 22 } },
  { Icon: icons.Visa, darkModeWhite: true, sx: { width: 38, height: 22 } },
  { Icon: icons.Mastercard, sx: { width: 30, height: 22 } },
];

export const PaymentIcons = () => {
  const theme = useTheme();
  const darkSelector = theme.getColorSchemeSelector('dark');

  return (
    <>
      {paymentIcons.map(({ Icon, darkModeWhite, sx }, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            ...(darkModeWhite && {
              [darkSelector]: { filter: 'brightness(0) invert(1)' },
            }),
          }}
        >
          <Icon sx={sx} />
        </Box>
      ))}
    </>
  );
};

const PaymentsList = () => (
  <Grid
    sx={{
      flexWrap: 'wrap',
      display: 'flex',
      flexDirection: 'row',
      gap: 3,
    }}
  >
    <PaymentIcons />
  </Grid>
);

const SummaryDetails = ({
  price = {},
  basketItemsWithPrice,
  displayItemsPriceDetails = true,
  displayInsurance = true,
  showMoreDetails = true,
  itemsContent,
  belowSummaryContent,
}) => {
  const { totalIncVatAmount, taxAmount } = price;
  const [accordionDetailsHeight, setAccordionDetailsHeight] = useState('auto');

  const handleAccordionChange = (_, isExpanded) => {
    if (isExpanded) {
      const summaryHeight = 115;
      const viewportHeight = window.innerHeight;

      // Calculate max height for AccordionDetails dynamically
      setAccordionDetailsHeight(`${viewportHeight - summaryHeight}px`);
    } else {
      setAccordionDetailsHeight('auto');
    }
  };

  return (
    <>
      {/* Desktop version */}
      <Paper sx={{ p: 2, mb: 2, display: { xs: 'none', md: 'block' } }}>
        <Typography variant='h3'>{SUMMARY_DETAILS.TITLE}</Typography>
        {itemsContent ??
          (displayItemsPriceDetails && (
            <CheckoutPriceList
              items={basketItemsWithPrice}
              displayInsurance={displayInsurance}
              showMoreDetails={showMoreDetails}
            />
          ))}
        <TotalPrice totalIPrice={totalIncVatAmount} taxAmount={taxAmount} />
        <PaymentsList />
      </Paper>
      {belowSummaryContent ? (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          {belowSummaryContent}
        </Box>
      ) : null}
      {/* Mobile Version */}
      <Portal>
        <Box
          sx={{
            display: { md: 'none' },
            position: 'sticky',
            bottom: 0,
            zIndex: 3,
            width: '100%',
          }}
        >
          <Accordion elevation={4} onChange={handleAccordionChange}>
            <StyledAccordionSummary
              expandIcon={<ArrowDropUp />}
              sx={{
                flexDirection: 'row-reverse',
                justifyContent: 'flex-end',
                p: 1,
              }}
            >
              <TotalPrice
                totalIPrice={totalIncVatAmount}
                taxAmount={taxAmount}
              />
            </StyledAccordionSummary>
            <AccordionDetails
              sx={{
                // Dynamic and depends of parcels number and mobile device screen size
                maxHeight: accordionDetailsHeight,
                // Enable inner scroll only
                overflowY: 'auto',
              }}
            >
              <Typography variant='h3'>{SUMMARY_DETAILS.TITLE}</Typography>
              {itemsContent ??
                (displayItemsPriceDetails && (
                  <CheckoutPriceList
                    items={basketItemsWithPrice}
                    displayInsurance={displayInsurance}
                    showMoreDetails={showMoreDetails}
                  />
                ))}
              <PaymentsList />
              {belowSummaryContent ? (
                <Box sx={{ display: { md: 'none' }, mt: 1 }}>
                  {belowSummaryContent}
                </Box>
              ) : null}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Portal>
    </>
  );
};

SummaryDetails.propTypes = {
  price: PropTypes.object,
  basketItemsWithPrice: PropTypes.arrayOf(PropTypes.object),
  displayItemsPriceDetails: PropTypes.bool,
  displayInsurance: PropTypes.bool,
  showMoreDetails: PropTypes.bool,
  itemsContent: PropTypes.node,
  belowSummaryContent: PropTypes.node,
};

export default SummaryDetails;
